import React, { useState } from 'react';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import {
  Box, Typography, Paper, Button, Chip, Collapse, Stack, Avatar,
  IconButton, Tooltip, CircularProgress, Divider, Badge
} from '@mui/material';
import {
  Category as CategoryIcon,
  LocationOn,
  Email,
  Phone,
  CalendarToday,
  Visibility,
  ExpandMore,
  ExpandLess,
  Business,
  Handyman,
  ShoppingBag,
  ThumbUp,
  ThumbDown,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Public,
  CheckCircle,
  Cancel
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { useAuth } from '../../../context/authContext';

// Configuración global para SweetAlert2
Swal.mixin({
  position: 'top',
  background: '#ffffff',
  backdrop: 'rgba(4, 136, 175, 0.1)',
  showConfirmButton: true,
  timerProgressBar: true,
});

// Paleta de colores profesional
const colors = {
  primary: '#09274A',
  secondary: '#0488AF',
  accent: '#1BBB55',
  warning: '#FF9800',
  error: '#C42251',
  lightBg: '#F5F9FC',
  textPrimary: '#2D3748',
  textSecondary: '#718096'
};

const ServiceCard = ({ 
  service, 
  loading,
  onEditClick,
  onDeleteClick
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const { user } = useAuth();

  const safeService = {
    id: service?.id || 0,
    title: service?.title || 'SERVICIO NO DISPONIBLE',
    description: service?.description || 'NO HAY DESCRIPCIÓN DISPONIBLE',
    type_publish: service?.type_publish || 'Servicio',
    type_service: service?.type_service || 'Oferta',
    category: service?.category || 'SIN CATEGORÍA',
    date_publish: service?.date_publish || new Date().toISOString(),
    contact: service?.contact || 'N/A',
    email: service?.email || 'N/A',
    location: service?.location || 'UBICACIÓN NO ESPECIFICADA',
    visits: service?.visits || 0,
    status: service?.status || 'inactive',
    company: service?.company || {
      business_name: 'Empresa no especificada',
      logo: '/default-company-logo.png'
    }
  };

  const formatDate = (dateString) => {
    try {
      if (!dateString) return 'FECHA NO DISPONIBLE';
      
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'FECHA NO VÁLIDA';
      
      const offset = date.getTimezoneOffset();
      date.setMinutes(date.getMinutes() + offset);
      
      const day = date.getDate();
      const month = date.toLocaleString('es-CL', { month: 'long' });
      const year = date.getFullYear();
      
      return `${day} de ${month} de ${year}`;
    } catch (error) {
      console.error('Error al formatear fecha:', error);
      return 'FECHA NO DISPONIBLE';
    }
  };

  const getTypeIcon = () => {
    switch(safeService.type_publish) {
      case 'Servicio':
        return <Handyman sx={{ color: colors.secondary }} />;
      case 'Bien/Producto':
        return <ShoppingBag sx={{ color: colors.accent }} />;
      default:
        return <Business sx={{ color: colors.primary }} />;
    }
  };

const getSubTypeIcon = () => {
    if (safeService.type_publish !== 'Servicio') return null;
    
    return safeService.type_service === 'Oferta' 
      ? < UnarchiveIcon sx={{ color: colors.secondary }} />  // Usando ArchiveIcon para "Oferta"
      : < ArchiveIcon sx={{ color: colors.secondary}} />; // Usando UnarchiveIcon para otros casos
};

  const handleDeleteClick = (id) => {
    setDeletingId(id);
    
    Swal.fire({
      title: '¿Eliminar publicación?',
      text: `¿Estás seguro que deseas eliminar "${safeService.title}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: colors.secondary,
      cancelButtonColor: colors.error,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        onDeleteClick(safeService.id)
          .then(() => {
            Swal.fire({
              title: 'Eliminado',
              text: 'La publicación se ha eliminado correctamente',
              icon: 'success',
              confirmButtonColor: colors.secondary,
              timer: 2000
            });
          })
          .catch(error => {
            Swal.fire({
              title: 'Error',
              text: error.message || 'Ocurrió un error al eliminar',
              icon: 'error',
              confirmButtonColor: colors.error
            });
          });
      }
      setDeletingId(null);
    });
  };

  const toggleDescription = () => {
    setShowFullDescription(!showFullDescription);
  };

  return (
    <Box sx={{ 
      position: 'relative',
      height: 'auto',
      minHeight: '400px',
      width: '800px',
      maxWidth: '800px',
      transition: 'transform 0.3s ease',
      '&:hover': {
        transform: 'translateY(-5px)',
        '& .company-logo': {
          transform: 'scale(1.05)',
          boxShadow: `0 0 20px ${colors.secondary}33`
        }
      }
    }}>
      <Paper elevation={3} sx={{
        borderRadius: '16px',
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: colors.lightBg
      }}>
        {/* Header con gradiente */}
        <Box sx={{
          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
          p: 2.5,
          color: 'white',
          position: 'relative'
        }}>
          {/* Botones de acción */}
          <Box sx={{ 
            position: 'absolute',
            top: 12,
            right: 12,
            display: 'flex',
            gap: 1,
          }}>
            {/* Botón Editar */}
            <Tooltip title="Editar" arrow>
              <IconButton
                size="small"
                onClick={() => onEditClick(service)}
                sx={{ 
                  color: colors.primary,
                  backgroundColor: '#FFC107',
                  '&:hover': { 
                    backgroundColor: '#FFA000',
                    transform: 'scale(1.1)'
                  },
                  transition: 'all 0.2s'
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            {/* Botón Eliminar */}
            <Tooltip title="Eliminar" arrow>
              <IconButton
                size="small"
                onClick={() => handleDeleteClick(service.id)}
                disabled={loading && deletingId === service.id}
                sx={{ 
                  color: 'white',
                  backgroundColor: colors.error,
                  '&:hover': { 
                    backgroundColor: '#A21B43',
                    transform: 'scale(1.1)'
                  },
                  transition: 'all 0.2s'
                }}
              >
                {loading && deletingId === service.id ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <DeleteIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Box>
          
          {/* Título y tipo */}
          <Box sx={{ pr: 6 }}>
            <Typography variant="h6" sx={{
              fontWeight: 700,
              fontSize: '1.2rem',
              lineHeight: 1.3,
              mb: 0.5,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {safeService.title}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {getTypeIcon()}
              <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>
                {safeService.type_publish}
                {safeService.type_publish === 'Servicio' && ` • ${safeService.type_service}`}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Cuerpo de la tarjeta */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          flex: 1,
          p: 0
        }}>
          {/* Logo de la compañía */}
          <Box sx={{
            width: { xs: '100%', md: '35%' },
            minHeight: { xs: '180px', md: 'auto' },
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: 3,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              height: '70%',
              width: '1px',
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              display: { xs: 'none', md: 'block' }
            }
          }}>
            <Box sx={{ position: 'relative' }}>
              <Avatar
                className="company-logo"
                src={safeService.company.logo}
                alt={safeService.company.business_name}
                sx={{
                  width: { xs: 120, md: 140 },
                  height: { xs: 120, md: 140 },
                  backgroundColor: 'white',
                  border: `3px solid ${colors.secondary}`,
                  boxShadow: `0 0 0 5px ${colors.secondary}1a`,
                  transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  '& .MuiAvatar-img': {
                    objectFit: 'contain',
                    padding: '15px'
                  }
                }}
              />
              <Box sx={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                backgroundColor: safeService.status === 'active' ? colors.accent : colors.error,
                color: 'white',
                borderRadius: '4px',
                px: 1,
                py: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                fontSize: '0.75rem',
                fontWeight: 600,
                boxShadow: `0 2px 8px rgba(0,0,0,0.2)`
              }}>
                {safeService.status === 'active' ? (
                  <CheckCircle fontSize="small" sx={{ fontSize: '1rem' }} />
                ) : (
                  <Cancel fontSize="small" sx={{ fontSize: '1rem' }} />
                )}
                {safeService.status === 'active' ? 'ACTIVO' : 'INACTIVO'}
              </Box>
            </Box>
          </Box>

          {/* Información del servicio */}
          <Box sx={{
            width: { xs: '100%', md: '65%' },
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
          }}>
            {/* Encabezado información */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ 
                fontWeight: 600,
                color: colors.primary,
                mb: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                <Business sx={{ color: colors.secondary }} />
                {safeService.company.business_name}
              </Typography>
              
              {/* Chips de información */}
              <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Chip 
                  label={safeService.category} 
                  size="small"
                  icon={<CategoryIcon fontSize="small" sx={{ color: colors.primary }} />}
                  sx={{ 
                    backgroundColor: `${colors.secondary}15`,
                    color: colors.primary,
                    fontWeight: 600,
                    borderRadius: '6px'
                  }}
                />
                
                {safeService.type_publish === 'Servicio' && (
                  <Chip 
                    label={safeService.type_service} 
                    size="small"
                    icon={getSubTypeIcon()}
                    sx={{ 
                      backgroundColor: safeService.type_service === 'Oferta' 
                        ? `${colors.secondary}15` 
                        : `${colors.secondary}15`,
                      color: safeService.type_service === 'Oferta' 
                        ? colors.secondary 
                        : colors.secondary,
                      fontWeight: 600,
                      borderRadius: '6px'
                    }}
                  />
                )}
                
                <Chip 
                  label={formatDate(safeService.date_publish)} 
                  size="small"
                  icon={<CalendarToday fontSize="small" sx={{ color: colors.primary }} />}
                  sx={{ 
                    backgroundColor: `${colors.secondary}15`,
                    color: colors.primary,
                    fontWeight: 500,
                    borderRadius: '6px'
                  }}
                />
              </Stack>
            </Box>

            {/* Descripción */}
            <Box sx={{
              flex: 1,
              mb: 2,
              position: 'relative',
              minHeight: '120px',
              maxHeight: showFullDescription ? 'none' : '120px',
              overflow: 'hidden'
            }}>
              <Typography 
                variant="body2" 
                onClick={toggleDescription}
                sx={{
                  fontSize: '0.95rem',
                  lineHeight: 1.7,
                  color: colors.textPrimary,
                  whiteSpace: 'pre-line',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    color: colors.textSecondary
                  }
                }}
              >
                {safeService.description}
              </Typography>

              {!showFullDescription && safeService.description.length > 150 && (
                <Box sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '50px',
                  background: `linear-gradient(to bottom, transparent, ${colors.lightBg})`,
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'center',
                  pt: 2
                }}>
                  <Button 
                    onClick={toggleDescription}
                    size="small"
                    sx={{ 
                      color: colors.secondary,
                      fontWeight: 500,
                      fontSize: '0.8rem',
                      textTransform: 'none',
                      backgroundColor: `${colors.lightBg} !important`
                    }}
                  >
                    Mostrar más
                  </Button>
                </Box>
              )}
            </Box>

            {/* Footer con visitas y botón */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 'auto',
              pt: 2
            }}>
              <Chip 
                label={`${safeService.visits} visitas`} 
                size="small"
                icon={<Visibility fontSize="small" sx={{ color: colors.primary }} />}
                sx={{ 
                  backgroundColor: `${colors.secondary}15`,
                  color: colors.primary,
                  fontWeight: 500,
                  borderRadius: '8px'
                }}
              />
              
              <Button
                variant="contained"
                endIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
                onClick={() => setIsExpanded(!isExpanded)}
                sx={{
                  background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
                  color: 'white',
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                  borderRadius: '8px',
                  px: 3,
                  py: 1,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 12px ${colors.secondary}80`
                  }
                }}
              >
                {isExpanded ? 'OCULTAR' : 'CONTACTO'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Panel de contacto desplegable */}
      <Collapse in={isExpanded} timeout="auto" unmountOnExit
        sx={{
          position: 'absolute',
          left: '16px',
          right: '16px',
          bottom: '80px',
          zIndex: 2,
          '& .MuiCollapse-wrapper': {
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: `0 4px 20px ${colors.primary}20`,
            border: `1px solid ${colors.secondary}30`,
            overflow: 'hidden'
          }
        }}
      >
        <Box sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" sx={{
            mb: 2,
            fontWeight: 700,
            color: colors.primary,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}>
            <Box sx={{
              width: '4px',
              height: '24px',
              background: `linear-gradient(to bottom, ${colors.primary}, ${colors.secondary})`,
              borderRadius: '2px'
            }}/>
            Información de contacto
          </Typography>

          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
            gap: 2.5
          }}>
            {/* Columna izquierda */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="caption" sx={{ 
                  color: colors.textSecondary, 
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mb: 0.5
                }}>
                  <Business fontSize="small" /> Empresa
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: colors.textPrimary }}>
                  {safeService.company.business_name}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="caption" sx={{ 
                  color: colors.textSecondary, 
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mb: 0.5
                }}>
                  <LocationOn fontSize="small" /> Ubicación
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: colors.textPrimary }}>
                  {safeService.location}
                </Typography>
              </Box>
            </Box>

            {/* Columna derecha */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="caption" sx={{ 
                  color: colors.textSecondary, 
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mb: 0.5
                }}>
                  <Email fontSize="small" /> Email
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: colors.textPrimary }}>
                  {safeService.email}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="caption" sx={{ 
                  color: colors.textSecondary, 
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mb: 0.5
                }}>
                  <Phone fontSize="small" /> Teléfono
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: colors.textPrimary }}>
                  {safeService.contact}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default ServiceCard;